import { HttpClient, RequestParams } from "../../utils/api/http-client";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import { IBatch, IBatchQuery } from "../../models/wms/batch/response";
import { ICreateBatch, IUpdateBatch, ITransferBatch, IWriteOffBatch } from "../../models/wms/batch/request";

/** Backend BaseFilter reads filters from filters[n][name|value|operation], not flat query keys. */
const buildBatchesListQuery = (query: IBatchQuery): Record<string, string | number | boolean> => {
  const q: Record<string, string | number | boolean> = {};
  if (query.page !== undefined) q.page = query.page;
  if (query.perPage !== undefined) q.perPage = query.perPage;

  const filterEntries: Array<[string, string | number | boolean]> = [];
  if (query.productId !== undefined && query.productId !== null)
    filterEntries.push(["productId", query.productId]);
  if (query.id !== undefined && query.id !== null)
    filterEntries.push(["id", query.id]);
  if (query.storeId !== undefined && query.storeId !== null)
    filterEntries.push(["storeId", query.storeId]);
  if (query.storableType !== undefined && query.storableType !== null)
    filterEntries.push(["storableType", query.storableType]);
  if (query.storableId !== undefined && query.storableId !== null)
    filterEntries.push(["storableId", query.storableId]);
  if (query.batchNumber !== undefined && query.batchNumber !== null && query.batchNumber !== "")
    filterEntries.push(["batchNumber", query.batchNumber]);
  if (query.expirationDate !== undefined && query.expirationDate !== null && query.expirationDate !== "")
    filterEntries.push(["expirationDate", query.expirationDate]);
  if (query.hasStock !== undefined && query.hasStock !== null)
    filterEntries.push(["hasStock", query.hasStock]);
  if (query.createdAt !== undefined && query.createdAt !== null && query.createdAt !== "")
    filterEntries.push(["createdAt", query.createdAt]);
  if (query.updatedAt !== undefined && query.updatedAt !== null && query.updatedAt !== "")
    filterEntries.push(["updatedAt", query.updatedAt]);
  if (query.search !== undefined && query.search !== null && query.search !== "")
    filterEntries.push(["search", query.search]);
  if (query.light !== undefined && query.light !== null)
    q.light = query.light;

  filterEntries.forEach(([name, value], i) => {
    q[`filters[${i}][name]`] = name;
    q[`filters[${i}][value]`] = value;
    q[`filters[${i}][operation]`] = "eq";
  });

  return q;
};

class WmsBatchService<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  getAllBatches = (query: IBatchQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IBatch>>({
      path: "/wms/batches",
      method: "GET",
      query: buildBatchesListQuery(query),
      ...params,
    });

  getBatch = (id: number, params?: RequestParams) =>
    this.http.request<IBatch>({
      path: `/wms/batches/${id}`,
      method: "GET",
      ...params,
    });

  createBatch = (data: ICreateBatch, params?: RequestParams) =>
    this.http.request<IBatch>({
      path: "/wms/batches",
      method: "POST",
      body: data,
      ...params,
    });

  updateBatch = (id: number, data: IUpdateBatch, params?: RequestParams) =>
    this.http.request<IBatch>({
      path: `/wms/batches/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteBatch = (id: number, params?: RequestParams) =>
    this.http.request<IBatch>({
      path: `/wms/batches/${id}`,
      method: "DELETE",
      ...params,
    });

  transferBatch = (id: number, data: ITransferBatch, params?: RequestParams) =>
    this.http.request<IBatch>({
      path: `/wms/batches/${id}/transfer`,
      method: "POST",
      body: data,
      ...params,
    });

  writeOffBatch = (id: number, data: IWriteOffBatch, params?: RequestParams) =>
    this.http.request<IBatch>({
      path: `/wms/batches/${id}/write-off`,
      method: "POST",
      body: data,
      ...params,
    });
}

export default WmsBatchService;
